// /admin/scim — SCIM 2.0 provisioning management (T193a).
//
// Two sections:
//   (a) Status — last successful SCIM request, total users provisioned.
//   (b) Bearer tokens — list with name / last_used / revoke button +
//       "Generate new token" dialog that shows the plaintext once on creation.

import { FormEvent, useState } from 'react';
import useSWR from 'swr';
import {
  Check,
  ClipboardCopy,
  Loader2,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  ADMIN_SCIM_STATUS_KEY,
  ADMIN_SCIM_TOKENS_KEY,
  SCIMStatus,
  SCIMToken,
  fetcher,
  scimApi,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

export function AdminScim() {
  const { data: status } = useSWR<SCIMStatus>(ADMIN_SCIM_STATUS_KEY, fetcher, {
    revalidateOnFocus: false,
  });

  const {
    data: tokens,
    error: tokensError,
    isLoading: tokensLoading,
    mutate: mutateTokens,
  } = useSWR<SCIMToken[]>(ADMIN_SCIM_TOKENS_KEY, fetcher, {
    revalidateOnFocus: false,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);

  const handleCreated = (tok: SCIMToken) => {
    setShowCreate(false);
    if (tok.token) setNewToken(tok.token);
    mutateTokens();
  };

  const handleRevoke = async (id: string) => {
    await scimApi.revokeToken(id);
    mutateTokens();
  };

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Users />}
        title="SCIM Provisioning"
        description={
          <>
            SCIM 2.0 bearer tokens let identity providers (Okta, Azure AD, OneLogin)
            auto-provision users into orbital-lint. Requires Trial or Enterprise license.
          </>
        }
        actions={
          showCreate ? null : (
            <button onClick={() => setShowCreate(true)} className={primaryBtnCls}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Generate token
            </button>
          )
        }
      />

      {/* Status card */}
      {status && (
        <div className="orb-card p-5 mb-8 grid grid-cols-2 gap-4">
          <div>
            <p className="orb-section-title text-[11px] mb-1">Users provisioned via SCIM</p>
            <p className="font-display text-2xl font-semibold text-fg">
              {status.total_provisioned}
            </p>
          </div>
          <div>
            <p className="orb-section-title text-[11px] mb-1">Active bearer tokens</p>
            <p className="font-display text-2xl font-semibold text-fg">
              {status.token_count}
            </p>
          </div>
        </div>
      )}

      {/* Copy-once banner */}
      {newToken && (
        <CopyOnceBanner token={newToken} onDismiss={() => setNewToken(null)} />
      )}

      {/* Create form */}
      {showCreate && (
        <CreateTokenForm
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Token list */}
      <div className="mb-4">
        <h2 className="font-semibold text-fg text-base mb-3">Bearer tokens</h2>
        {(() => {
          if (tokensLoading) {
            return (
              <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="text-sm">Loading tokens…</span>
              </div>
            );
          } else if (tokensError) {
            return <ErrorBanner />;
          } else if (!tokens || tokens.length === 0) {
            return (
              <div className="orb-card p-8 text-center text-fg-muted text-sm">
                No SCIM bearer tokens yet. Generate one to configure your IdP.
              </div>
            );
          } else {
            return <TokenTable tokens={tokens} onRevoke={handleRevoke} />;
          }
        })()}
      </div>

      {/* Setup guide */}
      <div className="orb-card p-5 mt-8 space-y-3">
        <p className="font-semibold text-fg text-sm">IdP configuration</p>
        <p className="text-xs text-fg-muted">
          Use these values when configuring SCIM 2.0 in your identity provider:
        </p>
        <div className="space-y-2">
          <div>
            <p className="orb-section-title text-[10px] mb-0.5">SCIM base URL</p>
            <code className="font-mono text-xs bg-bg-inset border border-border rounded px-2 py-1 text-fg select-all">
              {globalThis.location.origin}/scim/v2
            </code>
          </div>
          <div>
            <p className="orb-section-title text-[10px] mb-0.5">Authentication</p>
            <p className="text-xs text-fg-muted">
              HTTP Header — <code className="font-mono text-[11px]">Authorization: Bearer &lt;token&gt;</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy-once banner
// ---------------------------------------------------------------------------

function CopyOnceBanner({ token, onDismiss }: { readonly token: string; readonly onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      role="alert"
      className="orb-card mb-6 p-5 border-success/40 bg-success/5 space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-fg">Token created — copy it now</p>
          <p className="text-xs text-fg-muted mt-0.5">
            This value will not be shown again. Store it in your IdP as the SCIM bearer token.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="p-1 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg transition-colors"
          title="Dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <code className="flex-1 font-mono text-xs bg-bg-inset border border-border rounded px-3 py-2 truncate text-fg select-all">
          {token}
        </code>
        <button onClick={handleCopy} className={`${primaryBtnCls} shrink-0`}>
          {copied ? (
            <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Copied</>
          ) : (
            <><ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" /> Copy</>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create token form
// ---------------------------------------------------------------------------

interface CreateFormProps {
  readonly onCreated: (tok: SCIMToken) => void;
  readonly onCancel: () => void;
}

function CreateTokenForm({ onCreated, onCancel }: CreateFormProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr('Token name is required.');
      return;
    }
    setSaving(true);
    try {
      const created = await scimApi.createToken(name.trim());
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create token.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="orb-card mb-6 p-5 space-y-4">
      <p className="text-sm font-semibold text-fg">New SCIM bearer token</p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="scim-tok-name" className="orb-section-title text-[11px]">
          Token name
        </label>
        <input
          id="scim-tok-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
          placeholder="e.g. okta-prod, azure-ad-staging"
          autoFocus
        />
      </div>
      {err && <p role="alert" className="text-xs text-danger">{err}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Creating…</>
          ) : (
            'Generate token'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-fg-muted hover:text-fg transition-colors px-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Token table
// ---------------------------------------------------------------------------

interface TokenTableProps {
  readonly tokens: SCIMToken[];
  readonly onRevoke: (id: string) => void | Promise<void>;
}

function TokenTable({ tokens, onRevoke }: TokenTableProps) {
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this SCIM token? The IdP will no longer be able to provision users.')) return;
    setRevoking(id);
    try {
      await onRevoke(id);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Table>
      <THead>
        <TR>
          <TH>Name</TH>
          <TH>Last used</TH>
          <TH>Created</TH>
          <TH srOnly>Actions</TH>
        </TR>
      </THead>
      <TBody>
          {tokens.map((t) => (
            <TR key={t.id} muted={t.revoked}>
              <TD primary>
                {t.name}
                {t.revoked && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-danger/70">
                    revoked
                  </span>
                )}
              </TD>
              <TD mono>
                {t.last_used_at ? fmtDate(t.last_used_at) : <span className="italic">never</span>}
              </TD>
              <TD mono>
                {fmtDate(t.created_at)}
              </TD>
              <TD align="right">
                {!t.revoked && (
                  <button
                    onClick={() => handleRevoke(t.id)}
                    disabled={revoking === t.id}
                    className="p-1.5 rounded text-fg-muted hover:text-danger hover:bg-danger/10 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30"
                    title="Revoke token"
                  >
                    {revoking === t.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                )}
              </TD>
            </TR>
          ))}
      </TBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorBanner() {
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">Could not load SCIM tokens.</p>
      <p className="text-xs text-fg-muted mt-2">
        SCIM management requires org_admin role and a Trial or Enterprise license.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers + styles
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
